/**
 * PDF export for monthly payslip (fiche de paie mensuelle).
 * One page per employee with salary + commissions + travel bonuses + other bonuses − deductions.
 * Includes signature block (salarié + responsable) for bookkeeping.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTHS_FR = [
  "", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const fmtEur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtPct = (n) => `${((n || 0) * 100).toFixed(2)}%`;

/**
 * Generate and download the monthly payslip PDF for a given employee.
 * @param {Object} data - Response payload from GET /api/employees/{id}/payslip
 * @param {Object} [opts]
 * @param {string} [opts.companyName] - Company name printed in the header.
 */
export function generatePayslipPdf(data, opts = {}) {
  const pdf = new jsPDF({ format: "a4", unit: "mm" });
  const { employee, period, base_salary, commissions, travel_bonus, other_bonuses, deductions, overtime, gross_total } = data;
  const empName = `${employee.last_name || ""} ${employee.first_name || ""}`.trim();
  const periodLabel = `${MONTHS_FR[period.month]} ${period.year}`;
  const companyName = opts.companyName || "Bati Assistant";

  // Header
  pdf.setFontSize(17);
  pdf.setFont("helvetica", "bold");
  pdf.text("Fiche de paie mensuelle", 14, 18);

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(90);
  pdf.text(companyName, 196, 18, { align: "right" });
  pdf.setTextColor(0);

  // Horizontal line
  pdf.setDrawColor(220);
  pdf.line(14, 22, 196, 22);

  // Employee + period block
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "bold");
  pdf.text(empName || "—", 14, 30);

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(90);
  pdf.text(`Type : ${employee.employee_type || "—"}`, 14, 36);
  pdf.text(`Période : ${periodLabel}`, 14, 41);
  pdf.text(`Édité le ${new Date().toLocaleDateString("fr-FR")}`, 196, 41, { align: "right" });
  pdf.setTextColor(0);

  // Table 1: breakdown of earnings
  const baseNote = base_salary?.notes ? ` — ${base_salary.notes}` : "";
  const baseSubtitle = base_salary?.effective_from
    ? `Rémunération contractuelle (depuis le ${new Date(base_salary.effective_from).toLocaleDateString("fr-FR")})${baseNote}`
    : "Aucune rémunération contractuelle enregistrée";

  const commSubtitle = commissions?.seller_id
    ? `Vendeur rattaché : ${commissions.seller_name || "—"} · ${commissions.orders_count} commande(s) · Taux ${fmtPct(commissions.rate)} · CA HT payé ${fmtEur(commissions.paid_ht)}`
    : "Aucun vendeur rattaché à ce salarié";

  const travelSubtitle = travel_bonus?.days
    ? `${travel_bonus.days} jour(s) avec prime de petit déplacement`
    : "Aucun déplacement primé ce mois-ci";

  const earningsRows = [
    [
      { content: "Salaire de base", styles: { fontStyle: "bold" } },
      baseSubtitle,
      { content: fmtEur(base_salary?.monthly_salary || 0), styles: { halign: "right", fontStyle: "bold" } },
    ],
    [
      { content: "Commissions vendeur", styles: { fontStyle: "bold" } },
      commSubtitle,
      { content: fmtEur(commissions?.amount || 0), styles: { halign: "right", fontStyle: "bold", textColor: [49, 46, 129] } },
    ],
    [
      { content: "Primes de petit déplacement", styles: { fontStyle: "bold" } },
      travelSubtitle,
      { content: fmtEur(travel_bonus?.amount || 0), styles: { halign: "right", fontStyle: "bold" } },
    ],
  ];

  // Other bonuses (one row each, or none)
  if ((other_bonuses?.items || []).length === 0) {
    earningsRows.push([
      { content: "Primes manuelles", styles: { fontStyle: "bold" } },
      "Aucune prime manuelle ce mois-ci",
      { content: fmtEur(0), styles: { halign: "right", fontStyle: "bold" } },
    ]);
  } else {
    other_bonuses.items.forEach((b, i) => {
      earningsRows.push([
        i === 0 ? { content: "Primes manuelles", styles: { fontStyle: "bold" } } : "",
        `${b.label}${b.date ? ` · ${new Date(b.date).toLocaleDateString("fr-FR")}` : ""}`,
        { content: fmtEur(b.amount), styles: { halign: "right" } },
      ]);
    });
    earningsRows.push([
      "",
      { content: "Sous-total primes manuelles", styles: { fontStyle: "italic", textColor: 90 } },
      { content: fmtEur(other_bonuses.total), styles: { halign: "right", fontStyle: "bold" } },
    ]);
  }

  autoTable(pdf, {
    startY: 48,
    head: [["Rubrique", "Détail", "Montant"]],
    body: earningsRows,
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [49, 46, 129], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 102 },
      2: { cellWidth: 32, halign: "right" },
    },
    margin: { left: 14, right: 14 },
  });

  // Table 2: deductions (if any)
  let cursorY = pdf.lastAutoTable.finalY + 4;
  if ((deductions?.items || []).length > 0) {
    const dedRows = deductions.items.map((d) => [
      "",
      `${d.label}${d.date ? ` · ${new Date(d.date).toLocaleDateString("fr-FR")}` : ""}`,
      { content: `− ${fmtEur(d.amount)}`, styles: { halign: "right", textColor: [185, 28, 28] } },
    ]);
    dedRows.unshift([
      { content: "Retenues / PV", colSpan: 1, styles: { fontStyle: "bold", textColor: [185, 28, 28] } },
      { content: `${deductions.items.length} retenue(s) ce mois-ci`, styles: { textColor: 90 } },
      { content: `− ${fmtEur(deductions.total)}`, styles: { halign: "right", fontStyle: "bold", textColor: [185, 28, 28] } },
    ]);

    autoTable(pdf, {
      startY: cursorY,
      body: dedRows,
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 102 },
        2: { cellWidth: 32, halign: "right" },
      },
      margin: { left: 14, right: 14 },
    });
    cursorY = pdf.lastAutoTable.finalY + 4;
  }

  // Overtime / deficit summary (info, pas un montant — à finaliser par la paie)
  if (overtime && (overtime.overtime_hours > 0 || overtime.deficit_hours > 0 || overtime.month_delta_hours !== 0)) {
    const modeLabel = overtime.mode === "annual" ? "annuel" : "mensuel";
    const rateLabel = overtime.hourly_rate ? ` (taux horaire ${fmtEur(overtime.hourly_rate)})` : "";
    const overtimeRows = [[
      { content: "Heures supplémentaires", styles: { fontStyle: "bold", textColor: [5, 122, 85] } },
      { content: `Delta du mois : ${overtime.month_delta_hours >= 0 ? "+" : ""}${Number(overtime.month_delta_hours).toFixed(1)}h · Mode ${modeLabel}${rateLabel}`, styles: { textColor: 90 } },
      {
        content: overtime.overtime_hours > 0
          ? `+${Number(overtime.overtime_hours).toFixed(1)}h`
          : overtime.deficit_hours > 0
            ? `−${Number(overtime.deficit_hours).toFixed(1)}h`
            : "0.0h",
        styles: {
          halign: "right",
          fontStyle: "bold",
          textColor: overtime.overtime_hours > 0 ? [5, 122, 85] : overtime.deficit_hours > 0 ? [185, 28, 28] : 90,
        },
      },
    ]];
    autoTable(pdf, {
      startY: cursorY,
      body: overtimeRows,
      styles: { fontSize: 9, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 102 },
        2: { cellWidth: 32, halign: "right" },
      },
      margin: { left: 14, right: 14 },
    });
    cursorY = pdf.lastAutoTable.finalY + 4;
  }

  // Total brut
  autoTable(pdf, {
    startY: cursorY,
    body: [[
      { content: "Total brut du mois", styles: { fontStyle: "bold", fontSize: 11, fillColor: [238, 242, 255] } },
      { content: "", styles: { fillColor: [238, 242, 255] } },
      { content: fmtEur(gross_total), styles: { halign: "right", fontStyle: "bold", fontSize: 13, textColor: [49, 46, 129], fillColor: [238, 242, 255] } },
    ]],
    styles: { cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 102 },
      2: { cellWidth: 32 },
    },
    margin: { left: 14, right: 14 },
  });

  cursorY = pdf.lastAutoTable.finalY + 4;

  // Disclaimer
  pdf.setFontSize(8);
  pdf.setTextColor(120);
  pdf.text(
    "Document de synthèse interne — à transmettre à l'expert-comptable pour établir le bulletin de paie officiel.",
    14,
    cursorY + 3,
  );
  pdf.setTextColor(0);

  // Signature block (bottom of page)
  const pageHeight = pdf.internal.pageSize.getHeight();
  const sigY = Math.min(pageHeight - 42, cursorY + 20);

  pdf.setDrawColor(180);
  pdf.line(14, sigY, 95, sigY);
  pdf.line(115, sigY, 196, sigY);

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("Signature du salarié", 14, sigY + 5);
  pdf.text("Signature du responsable", 115, sigY + 5);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(120);
  pdf.text("Date : ____ / ____ / ______", 14, sigY + 12);
  pdf.text("Date : ____ / ____ / ______", 115, sigY + 12);

  // Footer
  pdf.setFontSize(7);
  pdf.setTextColor(150);
  pdf.text(`Généré par Bati Assistant — ${new Date().toLocaleString("fr-FR")}`, 14, pageHeight - 8);

  const fileName = `fiche-paie-${(employee.last_name || "salarie").toLowerCase()}-${period.year}-${String(period.month).padStart(2, "0")}.pdf`;
  pdf.save(fileName);
  return fileName;
}

export default generatePayslipPdf;
